import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ChatbotVoiceSettings,
  ChatbotVoiceSettingsUpdateInput,
  VoiceAsrProvider,
  VoiceLang,
  VoiceTtsProvider,
} from "@chat-yai/shared/voice-types";
import type { PlanLimitsJson } from "@chat-yai/shared";
import { voiceSettingsApi } from "@/api/endpoints/voiceSettings";
import { tenantsApi } from "@/api/endpoints/tenants";
import { ApiError } from "@/api/client";
import { translateError } from "@/i18n/errors";
import { useToast } from "@/contexts/ToastContext";
import { permissionTooltip } from "@/lib/rbac";

interface Props {
  chatbotId: string;
  canEdit: boolean;
}

const VOICE_LANGS: { value: VoiceLang; label: string }[] = [
  { value: "en-NZ", label: "English (New Zealand)" },
  { value: "en-AU", label: "English (Australia)" },
  { value: "en-US", label: "English (US)" },
  { value: "zh-CN", label: "Chinese (Simplified)" },
];

const VOICE_PRESETS: { value: string; label: string; lang: VoiceLang }[] = [
  { value: "BV700_streaming", label: "Default (Volcano BV700)", lang: "en-NZ" },
  { value: "en-NZ-MollyNeural", label: "Molly · en-NZ (Azure)", lang: "en-NZ" },
  { value: "en-NZ-MitchellNeural", label: "Mitchell · en-NZ (Azure)", lang: "en-NZ" },
  { value: "zh-CN-XiaoxiaoNeural", label: "Xiaoxiao · zh-CN (Azure)", lang: "zh-CN" },
];

function planLimitsSupportsVoice(limits: PlanLimitsJson | null | undefined): boolean {
  if (!limits || !limits.voice_web) return false;
  return (limits.voice_web.monthly_minutes ?? 0) > 0;
}

export function VoiceSection({ chatbotId, canEdit }: Props): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();

  const tenantQuery = useQuery({
    queryKey: ["tenants", "current"],
    queryFn: () => tenantsApi.current(),
  });
  const planSupportsVoice = planLimitsSupportsVoice(
    tenantQuery.data?.plan.limits_json,
  );

  const settingsQuery = useQuery({
    queryKey: ["voice-settings", chatbotId],
    queryFn: () => voiceSettingsApi.get(chatbotId),
  });

  const [draft, setDraft] = useState<ChatbotVoiceSettings | null>(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (settingsQuery.data) {
      setDraft(settingsQuery.data);
      setDirty(false);
    }
  }, [settingsQuery.data]);

  const saveMutation = useMutation({
    mutationFn: (input: ChatbotVoiceSettingsUpdateInput) =>
      voiceSettingsApi.update(chatbotId, input),
    onSuccess: (next) => {
      queryClient.invalidateQueries({ queryKey: ["voice-settings", chatbotId] });
      // Other consumers (Usage card, conversations list) rely on the same
      // settings indirectly via /admin/usage; refresh those too.
      queryClient.invalidateQueries({ queryKey: ["usage"] });
      toast.push("success", "Voice settings updated.");
      setDraft(next);
      setDirty(false);
    },
    onError: (err) => {
      toast.push(
        "error",
        translateError(err instanceof ApiError ? err.code : undefined),
      );
    },
  });

  if (settingsQuery.isLoading || !draft) {
    return (
      <section
        className="card"
        data-testid="voice-section"
        style={{ padding: 16, marginTop: 16 }}
      >
        <div className="empty">Loading voice settings…</div>
      </section>
    );
  }

  const update = <K extends keyof ChatbotVoiceSettings>(
    key: K,
    value: ChatbotVoiceSettings[K],
  ): void => {
    setDraft((d) => (d ? { ...d, [key]: value } : d));
    setDirty(true);
  };

  const onToggle = (next: boolean): void => {
    if (next && !planSupportsVoice) {
      toast.push("error", translateError("VOICE_PLAN_DISABLED"));
      return;
    }
    update("enabled", next);
  };

  const sessionMinutes = Math.round(draft.max_session_seconds / 60);
  const disabled = !canEdit;

  return (
    <section
      className="card"
      data-testid="voice-section"
      style={{ padding: 16, marginTop: 16 }}
    >
      <div className="sect-head">
        <h3>Voice</h3>
        {!planSupportsVoice ? (
          <span
            className="chip chip-warning"
            title="Upgrade your plan to enable web voice."
            data-testid="voice-plan-warning"
          >
            Plan does not include voice
          </span>
        ) : null}
      </div>
      <p className="help" style={{ marginTop: 0 }}>
        Let visitors hold a real-time voice conversation with this chatbot
        from any device with a microphone.
      </p>

      <div
        className="field-group"
        style={{ display: "flex", alignItems: "center", gap: 12 }}
      >
        <label
          htmlFor={`voice-enabled-${chatbotId}`}
          style={{ flex: 1, marginBottom: 0 }}
        >
          Enable web voice
        </label>
        <input
          id={`voice-enabled-${chatbotId}`}
          type="checkbox"
          checked={draft.enabled}
          disabled={disabled || !planSupportsVoice}
          onChange={(e) => onToggle(e.target.checked)}
          data-testid="voice-enabled-toggle"
          aria-label="Enable web voice"
        />
      </div>

      <div className="field-group">
        <label htmlFor={`voice-lang-${chatbotId}`}>Language</label>
        <select
          id={`voice-lang-${chatbotId}`}
          className="field-input"
          value={draft.lang}
          disabled={disabled || !draft.enabled}
          onChange={(e) => update("lang", e.target.value as VoiceLang)}
          data-testid="voice-lang-select"
        >
          {VOICE_LANGS.map((l) => (
            <option key={l.value} value={l.value}>
              {l.label}
            </option>
          ))}
        </select>
      </div>

      <div className="field-group">
        <label htmlFor={`voice-asr-${chatbotId}`}>Speech recognition</label>
        <select
          id={`voice-asr-${chatbotId}`}
          className="field-input"
          value={draft.asr_provider}
          disabled={disabled || !draft.enabled}
          onChange={(e) =>
            update("asr_provider", e.target.value as VoiceAsrProvider)
          }
          data-testid="voice-asr-select"
        >
          <option value="sensevoice">SenseVoice (default)</option>
          <option value="azure">Azure Speech (preview)</option>
        </select>
      </div>

      <div className="field-group">
        <label htmlFor={`voice-tts-${chatbotId}`}>Text-to-speech provider</label>
        <select
          id={`voice-tts-${chatbotId}`}
          className="field-input"
          value={draft.tts_provider}
          disabled={disabled || !draft.enabled}
          onChange={(e) =>
            update("tts_provider", e.target.value as VoiceTtsProvider)
          }
          data-testid="voice-tts-select"
        >
          <option value="volcano">Volcano (default)</option>
          <option value="azure">Azure Neural Voice</option>
        </select>
      </div>

      <div className="field-group">
        <label htmlFor={`voice-id-${chatbotId}`}>Voice / persona</label>
        <select
          id={`voice-id-${chatbotId}`}
          className="field-input"
          value={draft.voice_id}
          disabled={disabled || !draft.enabled}
          onChange={(e) => update("voice_id", e.target.value)}
          data-testid="voice-id-select"
        >
          {VOICE_PRESETS.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>
      </div>

      <div className="field-group">
        <label htmlFor={`voice-session-${chatbotId}`}>
          Maximum call length (minutes)
        </label>
        <input
          id={`voice-session-${chatbotId}`}
          type="number"
          min={1}
          max={30}
          step={1}
          className="field-input"
          value={sessionMinutes}
          disabled={disabled || !draft.enabled}
          onChange={(e) => {
            const minutes = Math.max(1, Math.min(30, Number(e.target.value || 1)));
            update("max_session_seconds", minutes * 60);
          }}
          data-testid="voice-session-input"
        />
      </div>

      <div className="field-group">
        <label htmlFor={`voice-included-${chatbotId}`}>
          Included minutes override
        </label>
        <input
          id={`voice-included-${chatbotId}`}
          type="number"
          min={0}
          step={1}
          className="field-input"
          placeholder="Use plan default"
          value={draft.included_minutes_override ?? ""}
          disabled={disabled || !draft.enabled}
          onChange={(e) => {
            const v = e.target.value.trim();
            update("included_minutes_override", v === "" ? null : Number(v));
          }}
          data-testid="voice-included-input"
        />
        <span className="help">
          Leave blank to inherit the plan&apos;s monthly minute allowance.
        </span>
      </div>

      <div
        style={{ marginTop: 12, display: "flex", justifyContent: "flex-end", gap: 8 }}
      >
        {!canEdit ? (
          <span className="help">{permissionTooltip("write")}</span>
        ) : null}
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={disabled || !dirty || saveMutation.isPending}
          onClick={() =>
            saveMutation.mutate({
              enabled: draft.enabled,
              lang: draft.lang,
              asr_provider: draft.asr_provider,
              tts_provider: draft.tts_provider,
              voice_id: draft.voice_id,
              max_session_seconds: draft.max_session_seconds,
              included_minutes_override: draft.included_minutes_override,
            })
          }
          data-testid="voice-save"
        >
          {saveMutation.isPending ? "Saving…" : "Save voice settings"}
        </button>
      </div>
    </section>
  );
}
